import React, { useState } from 'react';
import { RotateCcw, Pencil, Check, X, History } from 'lucide-react';
import { toast } from 'react-toastify';
import surveyApi from '../surveyApi';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null);

const defaultLabel = () => new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

// Same conversion as SurveyEditor: local datetime-local value -> UTC MySQL DATETIME.
const toUtcMysql = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

export const roundName = (r) => `Round ${r.round_number}${r.label ? ` · ${r.label}` : ''}`;

/**
 * Rounds = re-runs of the same survey. Starting a new round closes the current one
 * (its results are kept and stay comparable on the Trends tab) and reopens the
 * survey so everyone in the audience can respond again.
 */
export default function SurveyRoundsPanel({ surveyId, rounds, status, onChange }) {
  const [starting, setStarting] = useState(false);
  const [label, setLabel] = useState(defaultLabel());
  const [closesAt, setClosesAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  const current = rounds[rounds.length - 1];
  const canStart = current && current.completed > 0;

  const start = async () => {
    try {
      setBusy(true);
      const r = await surveyApi.adminStartRound(surveyId, { label, closes_at: toUtcMysql(closesAt) });
      toast.success(`Round ${r.round_number} is open.`);
      setStarting(false);
      onChange();
    } catch (e) {
      toast.error(e.message || 'Could not start a new round');
    } finally {
      setBusy(false);
    }
  };

  const saveLabel = async (r) => {
    try {
      await surveyApi.adminUpdateRound(surveyId, r.id, { label: editLabel });
      setEditingId(null);
      onChange();
    } catch (e) {
      toast.error(e.message || 'Could not rename round');
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 mb-6">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-slate-100 font-semibold flex items-center gap-2">
          <History className="w-4 h-4 text-amber-400" /> Rounds
        </h2>
        {!starting && (
          <button onClick={() => { setLabel(defaultLabel()); setClosesAt(''); setStarting(true); }}
            disabled={!canStart}
            title={canStart ? 'Close this round and re-run the survey' : 'The current round has no responses yet — publish it instead'}
            className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-1 disabled:opacity-40">
            <RotateCcw className="w-4 h-4" /> Start new round
          </button>
        )}
      </div>

      {starting && current && (
        <div className="mb-4 p-3 rounded border border-amber-700 bg-amber-900/20 text-sm text-amber-100 space-y-3">
          <p>
            <strong>{roundName(current)}</strong> ({current.completed} response{current.completed === 1 ? '' : 's'}) will
            be closed and kept as history. Round {current.round_number + 1} opens immediately with the same questions
            and languages, and everyone in the audience can respond again.
          </p>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <span className="block text-xs text-amber-200/80 mb-1">Round {current.round_number + 1} label</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100}
                className="px-3 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100" />
            </label>
            <label className="block">
              <span className="block text-xs text-amber-200/80 mb-1">Closes at (optional)</span>
              <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)}
                className="px-3 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100" />
            </label>
            <button onClick={start} disabled={busy}
              className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-60">
              {busy ? 'Starting…' : `Open Round ${current.round_number + 1}`}
            </button>
            <button onClick={() => setStarting(false)} disabled={busy}
              className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-700/60">
        {[...rounds].reverse().map((r) => {
          const isCurrent = current && r.id === current.id;
          const dates = [fmtDate(r.opened_at), fmtDate(r.closed_at)].filter(Boolean).join(' – ');
          return (
            <div key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                {editingId === r.id ? (
                  <>
                    <span className="text-slate-300">Round {r.round_number} ·</span>
                    <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} maxLength={100} autoFocus
                      className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-100" />
                    <button onClick={() => saveLabel(r)} className="text-emerald-300 hover:text-emerald-200"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="text-slate-100 font-medium">{roundName(r)}</span>
                    <button onClick={() => { setEditingId(r.id); setEditLabel(r.label || ''); }}
                      className="text-slate-500 hover:text-slate-200" title="Rename">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {isCurrent && (
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        status === 'open' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
                        current · {status}
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="text-slate-400 text-xs whitespace-nowrap">
                {dates || 'not opened yet'} · {r.completed} response{r.completed === 1 ? '' : 's'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
